/**
 * ParentDashboard — main parent-facing surface (#PL-WEB-PARENT).
 *
 * Sections (top to bottom):
 *   1. Header: "Welcome back, [parent first name]" + "Viewing practice
 *      for [nickname]" + kid switcher (if multiple kids).
 *   2. This week vs last week comparison strip.
 *   3. Per-goal trend: sparklines (trials/week + accuracy %), "Last
 *      practiced" relative date, expand for full trial history.
 *   4. Goals list: read-only rendering of all goals with status + area
 *      + "Last update" relative date.
 *   5. Per-activity summary: collapsed "Activity details ▾" expander.
 *   6. "What your kid did today" plain-language recap.
 *   7. SLP messages panel: thread list with unread badge + tap → thread
 *      view.
 *
 * Data sources:
 *   - Dashboard data: api-get-practice-rollup (#PL-EF-ROLLUP)
 *   - Threads list: api-list-parent-threads (this ticket)
 *   - Goals list: comes from the rollup's `goal_texts` field — avoids
 *     the parent hitting the goals table's RLS directly (per the
 *     issue's context block).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { supabase } from '../../lib/supabase/client';
import { formatShortDate } from '../../lib/effectiveslp/domain';
import { useParentAuth } from '../../lib/auth/useParentAuth';
import { buildParentRecap, buildWeekComparison } from './parentRecap';
import { EmptyState, MessagesIllustration } from '../../components/EmptyState';
import { ThreadListSkeleton } from '../../components/Skeleton';
import { humanError } from '../../lib/ui/errorCopy';
import type {
  GoalText,
  ListParentThreadsResponse,
  ParentAccountSummary,
  ParentThreadRow,
  PracticeSetSummary,
  RollupResponse,
  RollupItem,
} from './parentTypes';

interface Props {
  navigate: (path: string) => void;
  onOpenConsent: () => void;
  onOpenThread: (threadId: string) => void;
  onOpenGoalsView: () => void;
}

function goalIdFromRubric(rubric: unknown): string | null {
  if (rubric == null || typeof rubric !== 'object') return null;
  const obj = rubric as Record<string, unknown>;
  const gid = obj.goal_id ?? obj.goalId;
  if (typeof gid === 'string' && gid.length > 0) return gid;
  return null;
}

function titleFromRubric(rubric: unknown): string | null {
  if (rubric == null || typeof rubric !== 'object') return null;
  const obj = rubric as Record<string, unknown>;
  const t = obj.title;
  if (typeof t === 'string' && t.trim().length > 0) return t.trim();
  return null;
}

function relativeDate(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return '—';
  const days = Math.floor((Date.now() - ms) / (24 * 60 * 60 * 1000));
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
  return formatShortDate(iso);
}

function Sparkline({
  data,
  label,
}: {
  data: number[];
  label?: string;
}): ReactElement {
  const values = data.length ? data : [0];
  const max = Math.max(...values, 1);
  const min = Math.min(...values);
  const range = Math.max(max - min, 1);
  const width = 120;
  const height = 60;
  const padding = 4;
  const points = values.map((value, index) => {
    const x = values.length === 1
      ? width / 2
      : padding + (index / (values.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');
  const last = points.split(' ').at(-1) ?? '0,0';
  const [lastX, lastY] = last.split(',');
  return (
    <div className="sparkline" aria-label={label ?? 'Practice trend'}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <polyline fill="none" stroke="currentColor" strokeWidth="2" points={points} />
        <circle cx={lastX} cy={lastY} r="3" fill="currentColor" />
      </svg>
    </div>
  );
}

function firstNameFromEmail(email: string | null | undefined): string {
  if (!email) return 'parent';
  const local = email.split('@')[0] ?? '';
  if (local.length === 0) return 'parent';
  // Strip digits and treat as name.
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\d+/g, '').trim();
  if (cleaned.length === 0) return 'parent';
  return cleaned.split(' ')[0]!;
}

function trendArrow(trending: 'up' | 'down' | 'flat' | 'insufficient'): string {
  if (trending === 'up') return '↑';
  if (trending === 'down') return '↓';
  return '→';
}

export function ParentDashboard({
  navigate,
  onOpenConsent,
  onOpenThread,
  onOpenGoalsView,
}: Props): ReactElement {
  const { user, signOut } = useParentAuth();
  const [account, setAccount] = useState<ParentAccountSummary | null>(null);
  const [practiceSets, setPracticeSets] = useState<PracticeSetSummary[]>([]);
  const [selectedSetId, setSelectedSetId] = useState<string | null>(null);
  const [rollup, setRollup] = useState<RollupResponse | null>(null);
  const [threads, setThreads] = useState<ParentThreadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [activityDetailsOpen, setActivityDetailsOpen] = useState(false);
  const selectedSetIdRef = useRef<string | null>(null);

  // 1) Load parent_accounts row + practice sets + threads in parallel.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        // Ensure the parent account exists (idempotent).
        const { data: acctData, error: acctErr } = await supabase.functions
          .invoke<{ parent_account: ParentAccountSummary }>(
            'api-create-parent-account',
            { body: {} },
          );
        if (cancelled) return;
        if (acctErr) {
          setError(humanError(acctErr));
          setLoading(false);
          return;
        }
        const acct = acctData?.parent_account ?? null;
        if (!cancelled) setAccount(acct);

        // If consent not yet acknowledged, surface the consent screen.
        if (acct && !acct.consent_acknowledged_at) {
          if (!cancelled) {
            setLoading(false);
            onOpenConsent();
          }
          return;
        }

        // Load threads (any thread the parent is a member of).
        // The parent doesn't have RLS on practice_sets directly. We
        // rely on the threads list → practice_set_id to discover
        // the parent's sets. If the parent has no threads yet, we
        // surface the empty state.
        const threadsRes = await supabase.functions.invoke<
          ListParentThreadsResponse
        >('api-list-parent-threads', { body: {} });
        if (cancelled) return;
        if (threadsRes.error) {
          setError(humanError(threadsRes.error));
          setLoading(false);
          return;
        }
        const threadRows = threadsRes.data?.threads ?? [];
        if (!cancelled) setThreads(threadRows);

        // Resolve practice sets from threads. Each thread has a
        // practice_set_id; we harvest the unique set IDs and pull
        // the student nickname for the header.
        const setIds = Array.from(
          new Set(threadRows.map((t) => t.practice_set_id)),
        );
        if (setIds.length === 0) {
          if (!cancelled) {
            setPracticeSets([]);
            setLoading(false);
          }
          return;
        }
        const { data: psRows, error: psErr } = await supabase
          .from('practice_sets')
          .select('id, student_id, created_at, ends_at, students(nickname)')
          .in('id', setIds);
        if (cancelled) return;
        if (psErr) {
          setError(humanError(psErr));
          setLoading(false);
          return;
        }
        const sets: PracticeSetSummary[] = (psRows ?? []).map(
          (r) => {
            const row = r as unknown as {
              id: string;
              student_id: string | null;
              created_at: string;
              ends_at: string | null;
              students: { nickname: string | null } | null;
            };
            return {
              id: row.id,
              student_id: row.student_id,
              student_nickname: row.students?.nickname ?? null,
              created_at: row.created_at,
              ends_at: row.ends_at,
            };
          },
        );
        if (!cancelled) {
          setPracticeSets(sets);
          // Initialize the selected practice set from the parent's
          // current selection (if any) or fall back to the most
          // recently created. The ref keeps the dep array small —
          // we don't want this effect to refire on every selection
          // change.
          const current = selectedSetIdRef.current;
          const latest = current ?? sets[0]?.id ?? null;
          setSelectedSetId(latest);
          selectedSetIdRef.current = latest;
          setLoading(false);
        }
      } catch (err) {
        if (cancelled) return;
        setError(humanError(err));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onOpenConsent]);

  // 2) Load the rollup for the selected set.
  useEffect(() => {
    if (!selectedSetId) return;
    let cancelled = false;
    void (async () => {
      const { data, error: rollupErr } = await supabase.functions.invoke<
        RollupResponse
      >('api-get-practice-rollup', {
        body: { practice_set_id: selectedSetId },
      });
      if (cancelled) return;
      if (rollupErr) {
        setError(humanError(rollupErr));
        return;
      }
      setRollup(data ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedSetId]);

  const firstName = account?.display_name?.split(' ')[0]
    ?? firstNameFromEmail(user?.email);
  const selectedSet = practiceSets.find((s) => s.id === selectedSetId) ?? null;
  const kidNickname = selectedSet?.student_nickname ?? 'your kid';

  const activeGoals = useMemo(
    () => (rollup?.goal_texts ?? []).filter((g) => g.status === 'active'),
    [rollup],
  );

  const goalSparklines = useMemo(() => {
    const byGoal: Record<
      string,
      { trials: number[]; accuracy: number[] }
    > = {};
    for (const bucket of rollup?.sparkline_data ?? []) {
      const entry = byGoal[bucket.goal_id] ?? { trials: [], accuracy: [] };
      entry.trials.push(bucket.trials_attempted);
      entry.accuracy.push(bucket.accuracy_pct);
      byGoal[bucket.goal_id] = entry;
    }
    return byGoal;
  }, [rollup]);

  const lastPracticedByGoal = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const goal of rollup?.goal_texts ?? []) {
      let latest: string | null = null;
      for (const item of rollup?.per_item_rollup ?? []) {
        if (goalIdFromRubric(item.scoring_rubric) !== goal.goal_id) continue;
        if (
          item.last_attempted_at
          && (latest == null || item.last_attempted_at > latest)
        ) {
          latest = item.last_attempted_at;
        }
      }
      out[goal.goal_id] = latest;
    }
    return out;
  }, [rollup]);

  const recap = useMemo(() => buildParentRecap(rollup), [rollup]);
  const weekComparison = useMemo(
    () => buildWeekComparison(rollup),
    [rollup],
  );

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + t.unread_count, 0),
    [threads],
  );

  async function handleSignOut(): Promise<void> {
    try {
      await signOut();
      navigate('/parent/sign-in');
    } catch {
      // Best-effort; the auth state change will redirect on next render.
    }
  }

  if (loading) {
    return (
      <main className="screen parent-dashboard">
        <ThreadListSkeleton rows={3} />
      </main>
    );
  }

  if (error) {
    return (
      <main className="screen parent-dashboard">
        <header className="screen-header">
          <h1>Welcome back, {firstName}</h1>
        </header>
        <section className="empty-state" aria-live="assertive">
          <h2 className="empty-state-title">
            Couldn&rsquo;t load your dashboard
          </h2>
          <p className="empty-state-body">{error}</p>
          <button
            type="button"
            className="snap-button empty-state-cta"
            onClick={() => {
              // Force a full reload rather than fighting with the
              // existing cancellation guard; the lazy account
              // creation race is sensitive to effect-id stability.
              window.location.reload();
            }}
          >
            Try again
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => void handleSignOut()}
          >
            Sign out
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="screen parent-dashboard">
      <header className="screen-header">
        <h1>Welcome back, {firstName}</h1>
        <p className="tagline">Viewing practice for {kidNickname}</p>
        {practiceSets.length > 1 && (
          <label className="kid-switcher">
            Switch kid
            <select
              value={selectedSetId ?? ''}
              onChange={(e) => {
                const next = e.target.value;
                setSelectedSetId(next);
                selectedSetIdRef.current = next;
              }}
            >
              {practiceSets.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.student_nickname ?? s.id.slice(0, 8)}
                </option>
              ))}
            </select>
          </label>
        )}
      </header>

      {/* This week vs last week */}
      <section className="card" aria-label="Week comparison">
        <h2 className="onboarding-eyebrow">This week vs last week</h2>
        <p>
          This week: {weekComparison.thisWeek.sessions} session
          {weekComparison.thisWeek.sessions === 1 ? '' : 's'},{' '}
          {weekComparison.thisWeek.accuracyPct}% correct
        </p>
        <p>
          Last week: {weekComparison.lastWeek.sessions} session
          {weekComparison.lastWeek.sessions === 1 ? '' : 's'},{' '}
          {weekComparison.lastWeek.accuracyPct}% correct{' '}
          <span aria-label={`Trend ${weekComparison.trending}`}>
            {trendArrow(weekComparison.trending)}
          </span>
        </p>
      </section>

      {/* Per-goal trend */}
      <section>
        <h2 className="onboarding-eyebrow">How your kid is doing</h2>
        {activeGoals.length === 0 && (
          <p className="tagline">No active goals yet.</p>
        )}
        {activeGoals.map((goal: GoalText) => {
          const sparklines = goalSparklines[goal.goal_id];
          const items = (rollup?.per_item_rollup ?? []).filter(
            (it) => goalIdFromRubric(it.scoring_rubric) === goal.goal_id,
          );
          const isExpanded = expandedGoals.has(goal.goal_id);
          return (
            <div key={goal.goal_id} className="card stack-md">
              <div className="caseload-row-main">
                <span className="caseload-row-name">{goal.area}</span>
              </div>
              <p className="tagline">{goal.goal_text}</p>
              <div className="form-row">
                <Sparkline
                  data={sparklines?.trials ?? [0, 0, 0, 0]}
                  label="Trials per week"
                />
                <Sparkline
                  data={sparklines?.accuracy ?? [0, 0, 0, 0]}
                  label="Accuracy %"
                />
              </div>
              <p className="tagline">
                Last practiced: {relativeDate(lastPracticedByGoal[goal.goal_id] ?? null)}
              </p>
              <button
                type="button"
                className="link-button"
                onClick={() =>
                  setExpandedGoals((prev) => {
                    const next = new Set(prev);
                    if (next.has(goal.goal_id)) next.delete(goal.goal_id);
                    else next.add(goal.goal_id);
                    return next;
                  })}
              >
                {isExpanded
                  ? 'Hide trial history ▴'
                  : 'View trial history ▾'}
              </button>
              {isExpanded && (
                <div className="stack-md">
                  {items.length === 0 ? (
                    <p className="tagline">No trial history yet.</p>
                  ) : (
                    items.map((it: RollupItem) => (
                      <div key={it.item_id} className="card">
                        <span className="caseload-row-name">
                          {titleFromRubric(it.scoring_rubric)
                            ?? `Activity ${it.position + 1}`}
                        </span>
                        <span className="caseload-row-meta">
                          {it.trials_attempted} trials ·{' '}
                          {it.accuracy_pct}% accuracy
                        </span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </section>

      {/* Goals list (read-only — "IEP link") */}
      <section>
        <div className="button-row">
          <h2 className="onboarding-eyebrow">Goals</h2>
          <button
            type="button"
            className="link-button"
            onClick={onOpenGoalsView}
          >
            View all
          </button>
        </div>
        {(rollup?.goal_texts ?? []).slice(0, 3).map((g) => (
          <div key={g.goal_id} className="card">
            <div className="caseload-row-main">
              <span className="caseload-row-name">{g.area}</span>
              <span className="caseload-chip">{g.status}</span>
            </div>
            <p className="tagline">{g.goal_text}</p>
          </div>
        ))}
      </section>

      {/* Activity details expander */}
      <section>
        <button
          type="button"
          className="link-button"
          onClick={() => setActivityDetailsOpen((v) => !v)}
        >
          {activityDetailsOpen ? 'Hide activity details ▴' : 'Activity details ▾'}
        </button>
        {activityDetailsOpen && (
          <div className="stack-md">
            {(rollup?.per_item_rollup ?? []).length === 0 && (
              <p className="tagline">No activities yet.</p>
            )}
            {(rollup?.per_item_rollup ?? []).map((it) => (
              <div key={it.item_id} className="card">
                <span className="caseload-row-name">
                  {titleFromRubric(it.scoring_rubric)
                    ?? `Activity ${it.position + 1}`}
                </span>
                <span className="caseload-row-meta">
                  {it.trials_attempted} trials · {it.accuracy_pct}% accuracy
                  {it.last_attempted_at
                    ? ` · last attempted ${relativeDate(it.last_attempted_at)}`
                    : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* What your kid did today */}
      <section>
        <h2 className="onboarding-eyebrow">What your kid did today</h2>
        {recap ? (
          <div className="card">
            <p>
              <strong>{recap.whenLabel}</strong>
              {recap.durationMinutes > 0
                ? ` · ${recap.durationMinutes} minutes`
                : ''}
            </p>
            <p>{recap.summary}</p>
            {recap.isStale && (
              <p className="tagline">
                No new practice in the last week. Encourage {kidNickname} to
                try a quick session.
              </p>
            )}
          </div>
        ) : (
          <p className="tagline">No practice yet.</p>
        )}
      </section>

      {/* SLP messages panel */}
      <section>
        <h2 className="onboarding-eyebrow">Messages from your SLP</h2>
        {threads.length === 0 ? (
          <EmptyState
            illustration={<MessagesIllustration />}
            title="No messages yet"
            body="When your SLP sends a practice link or note, you'll see it here."
          />
        ) : (
          <div className="stack-md">
            {threads.map((t) => (
              <button
                key={t.thread_id}
                type="button"
                className="card stack-md"
                onClick={() => onOpenThread(t.thread_id)}
              >
                <div className="caseload-row-main">
                  <span className="caseload-row-name">
                    {t.last_message_at
                      ? `Last update ${relativeDate(t.last_message_at)}`
                      : 'No messages yet'}
                  </span>
                  {t.unread_count > 0 && (
                    <span className="caseload-chip" aria-label={`${t.unread_count} unread`}>
                      {t.unread_count}
                    </span>
                  )}
                </div>
                <p className="tagline">Tap to read the conversation</p>
              </button>
            ))}
          </div>
        )}
      </section>

      <div className="button-row">
        <button
          type="button"
          className="link-button"
          onClick={() => void handleSignOut()}
        >
          Sign out
        </button>
      </div>
      {/* Total unread counts are surfaced per-row; the dashboard
          exposes a per-thread badge only (no aggregate chip — keeps
          the visual noise low for parents). */}
      {totalUnread > 0 && (
        <p className="visually-hidden" aria-live="polite">
          {totalUnread} unread message{totalUnread === 1 ? '' : 's'}
        </p>
      )}
    </main>
  );
}
